import { createEffect, createMemo, createResource, createSignal, For, Show } from 'solid-js';
import { createStore } from 'solid-js/store';
import { A } from '@solidjs/router';
import Modal from './Modal';
import Text from './Text';
import ItemStackView from './ItemStackView';
import ItemSlotGrid from './inventory/ItemSlotGrid';
import { useToast } from './MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { useDebounced } from '../hooks/useDebounced';
import { useCraftingResolver } from '../services/craftingResolverContext';
import { resolveItemDisplayName } from '../services/itemDisplayName';
import { formatQuantityCompact } from '../services/quantityFormatter';
import { dimensionKey, dimensionLabel, ruleTypeIcon } from '../services/itemMatchRules';
import type { ApiCollisionDto, ItemStackDto, SellbotWizardDiffResponseDto, SellbotWizardRequest, SellbotWizardVaryingDimensionDto } from '../api/generated/schemas';
import styles from './ItemIdentityWizard.module.css';
import inventoryStyles from '../pages/Inventory.module.css';

// The scalar categories share one question per dimension; Lore/Enchantment/NbtContains ask one per
// differing entry instead (SellbotIdentityWizard already keys them per line/id/path, so grouping here
// is purely a heading, never a reason to collapse several entries into one toggle).
const CATEGORY_ORDER = ['Metadata', 'DisplayName', 'Signature', 'RepairCost', 'Lore', 'Enchantment', 'NbtContains'] as const;
const LIST_KINDS = ['Lore', 'Enchantment', 'NbtContains'] as const;

export interface WizardItemSource {
    placeholderKey: string;
    search: (query: string) => ItemStackDto[] | Promise<ItemStackDto[]>;
    stockOf?: (item: ItemStackDto) => number | undefined;
}

export interface ItemIdentityWizardProps {
    open: boolean;
    onClose: () => void;
    source: WizardItemSource;
    // Answers both the questions and the preview: the backend derives every rule from the picks and pins.
    diff: (request: SellbotWizardRequest) => Promise<{ ok: true; value: SellbotWizardDiffResponseDto } | { ok: false; error: string }>;
    onFinish: (request: SellbotWizardRequest) => Promise<{ ok: boolean; error?: string; collision?: ApiCollisionDto }>;
    // Lets the collision card link straight to the thing it collided with (an offer, a watch); omitted
    // when the caller has nowhere sensible to send the user (e.g. no per-entity page yet).
    collisionHref?: (entityId: string) => string;
}

type Step = 'pick' | 'questions' | 'preview';
type Answer = 'ignore' | 'match';

export default function ItemIdentityWizard(props: ItemIdentityWizardProps) {
    const { t } = useTranslation();
    const toast = useToast();
    const resolver = useCraftingResolver();

    const [step, setStep] = createSignal<Step>('pick');
    const [pickQuery, setPickQuery] = createSignal('');
    const [picked, setPicked] = createStore<ItemStackDto[]>([]);
    const [loadingDiff, setLoadingDiff] = createSignal(false);
    const [diff, setDiff] = createSignal<SellbotWizardDiffResponseDto | null>(null);
    const [answers, setAnswers] = createStore<Record<string, Answer>>({});
    const [finishing, setFinishing] = createSignal(false);
    const [collision, setCollision] = createSignal<ApiCollisionDto | null>(null);
    const [previewItem, setPreviewItem] = createSignal<ItemStackDto | null>(null);
    const [previewPending, setPreviewPending] = createSignal(false);

    const displayName = (item: ItemStackDto): string =>
        resolveItemDisplayName({ itemId: item.itemId, damage: item.metadata, snbt: item.snbt ?? undefined }, resolver());

    const reset = () => {
        setStep('pick');
        setPickQuery('');
        setPicked([]);
        setDiff(null);
        setAnswers({});
        setCollision(null);
    };

    const close = () => { reset(); props.onClose(); };

    const pickedKeys = createMemo(() => new Set(picked.map(item => item.key)));
    const firstItemId = createMemo(() => picked[0]?.itemId);

    const debouncedQuery = useDebounced(pickQuery, 200);
    // Fires on open with an empty query too, so the grid starts out browsable rather than blank until typed.
    // Gated on props.open, not just the query, so a closed wizard issues no search calls at all.
    const [rawResults] = createResource(() => props.open ? debouncedQuery() : undefined, (query) => props.source.search(query));
    const searchResults = createMemo(() => (rawResults() ?? [])
        .filter(item => firstItemId() === undefined || item.itemId === firstItemId())
        .filter(item => !pickedKeys().has(item.key)));

    const togglePick = (item: ItemStackDto) => {
        if (pickedKeys().has(item.key)) { setPicked(rows => rows.filter(row => row.key !== item.key)); return; }
        if (firstItemId() !== undefined && item.itemId !== firstItemId()) return;
        setPicked(picked.length, item);
    };

    const removePick = (item: ItemStackDto) => setPicked(rows => rows.filter(row => row.key !== item.key));

    const runDiff = async () => {
        if (picked.length === 0) return;
        setLoadingDiff(true);
        try {
            const res = await props.diff({ items: [...picked], pinned: [] });
            if (!res.ok) { toast?.error(res.error); return; }
            setDiff(res.value);
            setPreviewItem(res.value.preview);
            const defaults: Record<string, Answer> = {};
            for (const varying of res.value.varying) defaults[varyingKey(varying)] = 'ignore';
            setAnswers(defaults);
            setStep(res.value.varying.length > 0 ? 'questions' : 'preview');
        } finally {
            setLoadingDiff(false);
        }
    };

    const varyingKey = (varying: SellbotWizardVaryingDimensionDto) => dimensionKey(varying.kind, varying.index, varying.path ?? null);

    const groupedVarying = createMemo(() => {
        const current = diff();
        if (!current) return [] as { kind: string; entries: SellbotWizardVaryingDimensionDto[] }[];
        return CATEGORY_ORDER
            .map(kind => ({ kind, entries: current.varying.filter(v => v.kind === kind) }))
            .filter(group => group.entries.length > 0);
    });

    const isListKind = (kind: string) => (LIST_KINDS as readonly string[]).includes(kind);

    const entryLabel = (varying: SellbotWizardVaryingDimensionDto) => dimensionLabel(varying.kind, varying.index, varying.path ?? null, t);

    const wizardRequest = createMemo<SellbotWizardRequest>(() => ({
        items: [...picked],
        pinned: (diff()?.varying ?? [])
            .filter(varying => answers[varyingKey(varying)] === 'match')
            .map(varying => ({ kind: varying.kind, index: varying.index, path: varying.path ?? null })),
    }));

    const previewRequest = useDebounced(wizardRequest, 250);
    let previewSequence = 0;

    createEffect(() => {
        const request = previewRequest();
        if (!diff() || step() !== 'preview') return;
        const sequence = ++previewSequence;
        setPreviewPending(true);
        void props.diff(request).then(res => {
            if (sequence !== previewSequence) return;
            setPreviewPending(false);
            if (res.ok) setPreviewItem(res.value.preview);
        });
    });

    const previewStack = () => {
        const stack = previewItem();
        const current = diff();
        if (stack) return { itemId: stack.itemId, count: 1, damage: stack.metadata, snbt: stack.snbt ?? undefined };
        return { itemId: current?.itemId ?? 0, count: 1, damage: current?.metadata ?? 0, snbt: undefined };
    };

    const finish = async () => {
        const current = diff();
        if (!current) return;
        setFinishing(true);
        setCollision(null);
        try {
            const res = await props.onFinish(wizardRequest());
            if (!res.ok) {
                toast?.error(res.error ?? t('ITEM_WIZARD_FINISH_FAILED'));
                setCollision(res.collision ?? null);
                return;
            }
            close();
        } finally {
            setFinishing(false);
        }
    };

    const STEP_ORDER: Step[] = ['pick', 'questions', 'preview'];
    const stepIndex = () => STEP_ORDER.indexOf(step());

    return (
        <Modal open={props.open} onClose={close} maxWidth="40rem"
            title={<Text val="ITEM_WIZARD_TITLE" />}
            subtitle={<span>{t('ITEM_WIZARD_STEP', stepIndex() + 1, STEP_ORDER.length)} · {t('ITEM_WIZARD_STEP_' + step().toUpperCase())}</span>}>
            <div class="v" style={{ gap: 'var(--spacing-lg)' }}>
                <Show when={step() === 'pick'}>
                    <div class="v" style={{ gap: 'var(--spacing-md)' }}>
                        <p class={styles.helper}><Text val="ITEM_WIZARD_PICK_HINT" /></p>
                        <input type="text" class={styles.searchInput} value={pickQuery()}
                            placeholder={t(props.source.placeholderKey)}
                            onInput={(e) => setPickQuery(e.currentTarget.value)} />
                        <Show when={picked.length > 0}>
                            <div class={styles.chipRow}>
                                <For each={picked}>{(item) => (
                                    <button type="button" class={styles.chip} onClick={() => removePick(item)}>
                                        <ItemStackView stack={{ itemId: item.itemId, count: 1, damage: item.metadata, snbt: item.snbt ?? undefined }} size={24} bare />
                                        <span>{displayName(item)}</span>
                                        <i class="fa-solid fa-xmark" />
                                    </button>
                                )}</For>
                            </div>
                        </Show>
                        <Show when={firstItemId() !== undefined}>
                            <span class={styles.helper}><Text val="ITEM_WIZARD_SAME_MATERIAL_HINT" /></span>
                        </Show>
                        <Show when={!(searchResults().length === 0 && rawResults.loading)}>
                            <ItemSlotGrid
                                emptyMessage={<><i class="fa-solid fa-magnifying-glass" /> <Text val="ITEM_WIZARD_NO_MATCH" /></>}
                                items={searchResults().map((item) => {
                                    const stock = props.source.stockOf?.(item);
                                    return {
                                        key: item.key,
                                        label: displayName(item),
                                        stack: { itemId: item.itemId, count: 1, damage: item.metadata, snbt: item.snbt ?? undefined },
                                        badge: stock !== undefined ? <span class={inventoryStyles.badge}>{formatQuantityCompact(stock, resolver()?.getItemById(item.itemId, item.metadata)?.stackSize)}</span> : undefined,
                                    };
                                })}
                                onSelect={(key) => {
                                    const item = searchResults().find((s) => s.key === key);
                                    if (item) togglePick(item);
                                }}
                            />
                        </Show>
                        <div class="h" style={{ 'justify-content': 'flex-end' }}>
                            <button type="button" class="btn btn-primary" disabled={picked.length === 0 || loadingDiff()} onClick={() => void runDiff()}>
                                <Show when={loadingDiff()} fallback={<><Text val="ITEM_WIZARD_NEXT" /> <i class="fa-solid fa-arrow-right" /></>}>
                                    <i class="fa-solid fa-spinner fa-spin" />
                                </Show>
                            </button>
                        </div>
                    </div>
                </Show>

                <Show when={step() === 'questions' && diff()}>
                    {(current) => (
                        <div class="v" style={{ gap: 'var(--spacing-lg)' }}>
                            <Show when={current().agreedCount > 0}>
                                <p class={styles.helper}><i class="fa-solid fa-circle-check" /> {t('ITEM_WIZARD_AGREED', current().agreedCount)}</p>
                            </Show>
                            <For each={groupedVarying()}>{(group) => (
                                <div class={styles.categoryBlock}>
                                    <div class={styles.categoryHead}>
                                        <i class={`fa-solid ${ruleTypeIcon(group.kind)}`} aria-hidden="true" />
                                        <span>{t(isListKind(group.kind) ? 'SELLBOT_CATEGORY_' + group.kind : 'SELLBOT_RULE_TYPE_' + group.kind)}</span>
                                    </div>
                                    <For each={group.entries}>{(varying) => (
                                        <div class={styles.questionCard}>
                                            <div class={styles.questionHead}>
                                                <span class={styles.questionLabel}>{entryLabel(varying)}</span>
                                                <span class={styles.helper}>
                                                    <Text val="ITEM_WIZARD_SAMPLES" />: {varying.sampleValues.map(v => v ?? t('SELLBOT_DIM_ABSENT')).join(', ')}
                                                </span>
                                            </div>
                                            <div class={styles.answerRow}>
                                                <button type="button" class={`${styles.answerBtn} ${answers[varyingKey(varying)] !== 'match' ? styles.answerBtnActive : ''}`}
                                                    onClick={() => setAnswers(varyingKey(varying), 'ignore')}>
                                                    <Text val="ITEM_WIZARD_ANSWER_ANY" />
                                                </button>
                                                <button type="button" class={`${styles.answerBtn} ${answers[varyingKey(varying)] === 'match' ? styles.answerBtnActive : ''}`}
                                                    onClick={() => setAnswers(varyingKey(varying), 'match')}>
                                                    <Text val="ITEM_WIZARD_ANSWER_EXACT" />
                                                </button>
                                            </div>
                                        </div>
                                    )}</For>
                                </div>
                            )}</For>
                            <div class="h" style={{ 'justify-content': 'space-between' }}>
                                <button type="button" class="btn btn-secondary" onClick={() => setStep('pick')}>
                                    <i class="fa-solid fa-arrow-left" /> <Text val="ITEM_WIZARD_BACK" />
                                </button>
                                <button type="button" class="btn btn-primary" onClick={() => setStep('preview')}>
                                    <Text val="ITEM_WIZARD_NEXT" /> <i class="fa-solid fa-arrow-right" />
                                </button>
                            </div>
                        </div>
                    )}
                </Show>

                <Show when={step() === 'preview' && diff()}>
                    <div class="v" style={{ gap: 'var(--spacing-lg)' }}>
                        <div class={styles.previewBox} classList={{ [styles.previewStale]: previewPending() }}>
                            <ItemStackView stack={previewStack()} inlineTooltip />
                        </div>
                        <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <For each={diff()!.varying}>{(varying) => (
                                <span class={styles.summaryLine}>
                                    <i class={`fa-solid ${answers[varyingKey(varying)] === 'match' ? 'fa-lock' : 'fa-infinity'}`} />
                                    {entryLabel(varying)}: {t(answers[varyingKey(varying)] === 'match' ? 'ITEM_WIZARD_ANSWER_EXACT' : 'ITEM_WIZARD_ANSWER_ANY')}
                                </span>
                            )}</For>
                        </div>
                        <Show when={collision()}>
                            {(current) => (
                                <div class={styles.collisionCard}>
                                    <div class="h hcenter" style={{ gap: 'var(--spacing-md)' }}>
                                        <Show when={current().item}>
                                            {(item) => <ItemStackView stack={{ itemId: item().itemId, count: 1, damage: item().metadata, snbt: item().snbt ?? undefined }} size={32} bare />}
                                        </Show>
                                        <div class="v" style={{ gap: 'var(--spacing-xs)', flex: 1 }}>
                                            <span class={styles.collisionTitle}><i class="fa-solid fa-triangle-exclamation" /> <Text val="ITEM_WIZARD_COLLISION_TITLE" /></span>
                                            <span class={styles.helper}>{t('ITEM_WIZARD_COLLISION_WITH', current().label)}</span>
                                        </div>
                                        <Show when={props.collisionHref}>
                                            {(href) => <A href={href()(current().entityId)} class="btn btn-sm btn-secondary" onClick={close}><Text val="ITEM_WIZARD_COLLISION_VIEW" /></A>}
                                        </Show>
                                    </div>
                                </div>
                            )}
                        </Show>
                        <div class="h" style={{ 'justify-content': 'space-between' }}>
                            <button type="button" class="btn btn-secondary" onClick={() => setStep(diff()!.varying.length > 0 ? 'questions' : 'pick')}>
                                <i class="fa-solid fa-arrow-left" /> <Text val="ITEM_WIZARD_BACK" />
                            </button>
                            <button type="button" class="btn btn-primary" disabled={finishing()} onClick={() => void finish()}>
                                <Show when={finishing()} fallback={<><i class="fa-solid fa-check" /> <Text val="ITEM_WIZARD_CONFIRM" /></>}>
                                    <i class="fa-solid fa-spinner fa-spin" />
                                </Show>
                            </button>
                        </div>
                    </div>
                </Show>
            </div>
        </Modal>
    );
}
